import {useCallback, useMemo} from 'react';

import {useDispatch} from 'react-redux';

import {EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {useIsMinDesktop} from '~/shared/hooks/deviceInfo';
import {pushRoute} from '~/shared/router';
import {setCurrentModal, setUserReportOrderDetails} from '~/shared/store/actions';
import {ActiveOrderState} from '~/shared/store/models';
import {OrderWithMenuVisibility} from '~/shared/store/models/User/UserReport';

import {SideMenuOrder} from './useSideMenuHandlers';

export type MenuEventsProps = {
  onShowOrderClick: (order: SideMenuOrder) => Promise<void>;
  onAddEditReviewClick: (order: SideMenuOrder) => Promise<void>;
  onAddDisplayReviewClick: (order: SideMenuOrder) => Promise<void>;
  onDownloadDealInvoiceClick: (inoviceUrls: SideMenuOrder['inoviceUrls']) => void;
  onDebitTransferClick: (order: SideMenuOrder) => void;
};

const useMenuList = (order: ActiveOrderState | (OrderWithMenuVisibility & {id: number}), menuEvents?: MenuEventsProps) => {
  const dispatch = useDispatch();
  const isDesktop = useIsMinDesktop();
  const {
    shouldShowOrderMenuButton,
    shouldShowAddAReviewButton,
    shouldShowEditAReviewButton,
    shouldShowDisplayReviewButton,
    shouldShowDebitTransferMenuButton,
    shouldShowDownloadInvoiceMenuButton,
  } = order;

  const {
    onShowOrderClick,
    onAddEditReviewClick,
    onDownloadDealInvoiceClick,
    onDebitTransferClick,
    onAddDisplayReviewClick,
  } = menuEvents || EMPTY_OBJECT;

  const onCancelClick = useCallback(
    async () => {
      await dispatch(setUserReportOrderDetails(order));

      if (!isDesktop) {
        pushRoute('/cancel-active-order', {keepAllQueries: true});
        return;
      }

      dispatch(
        setCurrentModal('cancel_active_order_modal', {
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  const memoizedList = useMemo(() => {
    const list: unknown[] = 'cancellationEnable' in order && order.cancellationEnable
      ? [
          {
            name: 'show_order',
            onClick: () => onShowOrderClick(order),
          },
          {
            name: 'cancel_order',
            onClick: () => onCancelClick(),
          },
        ]
      : [
          {
            name: 'show_order',
            shouldHide: !shouldShowOrderMenuButton,
            onClick: () => {
              onShowOrderClick(order);
            },
          },

          {
            name: 'add_feedback',
            shouldHide: !shouldShowAddAReviewButton,
            onClick: () => {
              onAddEditReviewClick(order);
            },
          },
          {
            name: 'edit_feedback',
            shouldHide: !shouldShowEditAReviewButton,
            onClick: () => {
              onAddEditReviewClick(order);
            },
          },
          {
            name: 'display_feedback',
            shouldHide: !shouldShowDisplayReviewButton,
            onClick: () => {
              onAddDisplayReviewClick(order);
            },
          },
          {
            name: 'debit_transfer',
            shouldHide: !shouldShowDebitTransferMenuButton,
            onClick: () => {
              onDebitTransferClick(order);
            },
          },
          {
            name: 'download_deal_invoice',
            shouldHide: !shouldShowDownloadInvoiceMenuButton,
            onClick: () => {
              onDownloadDealInvoiceClick(order?.inoviceUrls);
            },
          },
        ];

    return list.reduce((allMenuItems: unknown[], menuItem: any) => {
      if (!menuItem.shouldHide) {
        allMenuItems.push(menuItem);
      }
      return allMenuItems;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAddEditReviewClick, onDebitTransferClick, onDownloadDealInvoiceClick, onShowOrderClick, order]);

  return memoizedList;
};

export default useMenuList;
